import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { amountForQuantity, formatINR, parseQuantity, quantityToWire } from '@shared/money';
import { cleanLitresInput, saveBlockers } from '@shared/fill';
import { api, actionKey } from '../../api';
import { useSession } from '../../session';
import type { FulfilmentView } from '../../types';
import { useClaims } from '../shared/useClaims';
import { ConfirmDialog, Empty, ErrorBox, Field, Loading, inr, litres, rupeesToPaise, useBusy } from '../../ui';
import { Icon, NumberPlate, Ticket, clockTime, plateText, rs } from '../../brand';
const QrScanner = lazy(() => import('./QrScanner').then((m) => ({ default: m.QrScanner })));

interface AttendantView {
  fullTank?: boolean;
  receiptScanningEnabled?: boolean;
  indentId: string;
  number: string;
  status: string;
  valid: boolean;
  problem?: { code: string; message: string };
  buyerName: string;
  personName: string;
  vehicleRegistration?: string;
  fuelLabel?: string;
  itemDescription?: string;
  limitType: 'AMOUNT' | 'QUANTITY';
  maxAmountPaise?: string;
  maxQuantity?: string;
  unit: string;
  unitPricePaise?: string;
  approxQuantity?: string;
  expiresAt?: string;
  locationName: string;
  requireReceiptPhoto: boolean;
  requireOdometer: boolean;
  claimedByMe?: boolean;
}

interface OpenClaim {
  indentId: string;
  number: string;
  personName: string;
  vehicleRegistration?: string;
  fuelType?: string;
  limit: string;
  claimedAt: string;
  claimedBy: string;
  stale: boolean;
}

export function Station() {
  const { me, active, can, refresh } = useSession();
  if (!me || !active) return <Loading />;
  const isManager = can('supplier.staff.manage', 'supplier.claims.resolve');
  // Managers still choose the shared device's pump and unlock with their PIN.
  // PIN state and fuel-entry permission are separate: a valid PIN must never
  // silently return to the same form just because the role lacks permission.
  if (!me.station && isManager) return <StationSetup onDone={() => void refresh()} />;
  if (me.station && !me.station.actingUserId) return <StaffSwitch />;
  if (!can('redeem.validate')) return me.station ? <FuelEntryAccessNeeded /> : <Empty>Only pump staff can enter fuel given. Ask your pump manager for access.</Empty>;
  return <Counter />;
}

function FuelEntryAccessNeeded() {
  const { refresh, can } = useSession();
  const { busy, error, run } = useBusy();
  return <div className="card stack">
    <h1>Fuel entry access needed</h1>
    <p>Your PIN was accepted. Your staff role does not allow fuel entries. Ask the pump manager to add the Pump attendant role if you need to fill fuel.</p>
    {can('supplier.claims.resolve') ? <Link className="btn" to="/supplier">Open pump accounts</Link> : null}
    <button className="btn" disabled={busy} onClick={() => void run(async () => { await api.post('/api/station/lock', {}, { orgId: null }); await refresh(); })}>Change staff member</button>
    <ErrorBox error={error} />
  </div>;
}

function StationSetup({ onDone }: { onDone: () => void }) {
  const locations = useQuery({ queryKey: ['locations'], queryFn: () => api.get<{ _id: string; name: string }[]>('/api/locations') });
  const [loc, setLoc] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <h1>Set up this device for fuel entries</h1>
      <p>Pump staff use this page to check a customer’s indent and record the fuel given.</p>
      <ol>
        <li>Choose the pump where this phone, tablet or computer will be used.</li>
        <li>Each staff member selects their name and enters their own PIN.</li>
        <li>They scan the driver’s QR code, fill fuel, then save the litres and amount.</li>
      </ol>
      <p className="muted">Every fuel entry shows which staff member saved it. Staff do not need an SMS code each time they use this shared device.</p>
      {locations.isLoading ? <Loading label="Loading your pumps…" /> : null}
      <ErrorBox error={locations.error} />
      {locations.data?.length === 0 ? <Empty>Add a pump under “Pump setup” first. Then return here to choose it.</Empty> : null}
      <Field label="Pump">
        <select value={loc} onChange={(e) => setLoc(e.target.value)}>
          <option value="">Choose your pump</option>
          {locations.data?.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
        </select>
      </Field>
      <ErrorBox error={error} />
      <button className="btn primary" disabled={!loc || busy} onClick={() => void run(async () => { await api.post('/api/station/setup', { locationId: loc }); onDone(); })}>Continue to staff PIN</button>
    </div>
  );
}

function StaffSwitch() {
  const { refresh, me } = useSession();
  const staff = useQuery({ queryKey: ['station-staff'], queryFn: () => api.get<{ membershipId: string; name: string; roles: string[]; pinSet: boolean }[]>('/api/station/staff', { orgId: null }) });
  const [who, setWho] = useState<{ membershipId: string; name: string } | null>(null);
  const [pin, setPin] = useState('');
  const { busy, error, run } = useBusy();
  return (
    <div className="card">
      <h1>Choose your name to start</h1>
      <p className="muted">You are at {me?.station?.locationName}. Select your name and enter your staff PIN to record fuel given.</p>
      {staff.isLoading ? <Loading label="Loading pump staff…" /> : null}
      <ErrorBox error={staff.error} />
      {staff.data?.length === 0 ? <Empty>No staff added to this pump yet. Ask the pump manager to add staff in “Pump setup”.</Empty> : null}
      {staff.data?.some((s) => !s.pinSet) ? <p className="muted">If you cannot select your name, ask the pump manager to set your PIN in “Pump setup”.</p> : null}
      {!who ? (
        <ul className="list">
          {staff.data?.map((s) => (
            <li key={s.membershipId}>
              <button className="btn block" disabled={!s.pinSet} onClick={() => setWho(s)}>{s.name}{!s.pinSet ? ' (PIN not set)' : ''}</button>
            </li>
          ))}
        </ul>
      ) : (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); void run(async () => { await api.post('/api/station/switch', { membershipId: who.membershipId, pin }, { orgId: null }); setPin(''); await refresh(); }); }}>
          <p><strong>{who.name}</strong></p>
          <Field label="PIN"><input className="big-input" type="password" inputMode="numeric" autoComplete="off" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus /></Field>
          <ErrorBox error={error} />
          <button className="btn primary block" disabled={busy || pin.length < 4}>Start fuel entry</button>
          <button type="button" className="btn ghost block" onClick={() => { setWho(null); setPin(''); }}>Choose another name</button>
        </form>
      )}
      <hr />
      <p className="muted">Finished using this shared device? Sign out below. A manager must sign in to set it up again.</p>
      <button className="btn ghost small" onClick={() => void run(async () => { await api.post('/api/station/end', {}, { orgId: null }); window.location.href = '/login'; })}>Sign out of this device</button>
    </div>
  );
}

type Step = { s: 'enter' } | { s: 'check'; view: AttendantView; token: { qr?: string; shortCode?: string } } | { s: 'supply'; view: AttendantView } | { s: 'done'; fulfilment: FulfilmentView; view: AttendantView };

function Counter() {
  const { me, refresh } = useSession();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>({ s: 'enter' });
  const [code, setCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const claims = useClaims<OpenClaim>('my-claims', 30_000);
  const { busy, error, run, setError } = useBusy();
  const [confirmClaim, setConfirmClaim] = useState(false);

  const validate = useCallback(
    (token: { qr?: string; shortCode?: string }) =>
      run(async () => {
        const view = await api.post<AttendantView>('/api/redeem/validate', token);
        setStep({ s: 'check', view, token });
      }),
    [run],
  );
  const onScan = useCallback((text: string) => { setScanning(false); void validate({ qr: text }); }, [validate]);

  const reset = () => { setStep({ s: 'enter' }); setCode(''); setError(null); void qc.invalidateQueries({ queryKey: ['my-claims'] }); };

  return (
    <div className="stack">
      {me?.station ? (
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button className="btn small" onClick={() => void api.post('/api/station/lock', {}, { orgId: null }).then(() => refresh())}><Icon name="people" size={18} />Change staff member</button>
        </div>
      ) : null}

      {step.s === 'enter' ? (
        <>
          <div>
            <h1>Fill fuel</h1>
            <p className="muted">Ask the driver for the QR code or 8-digit code on their approved indent.</p>
            <p>Check the code → check the vehicle and fuel limit → fill fuel → save the litres and amount.</p>
          </div>
          <div className="stack">
            {scanning ? <Suspense fallback={<Loading label="Opening camera…" />}><QrScanner onResult={onScan} onClose={() => setScanning(false)} /></Suspense> : (
              <button className="scan-hero" onClick={() => setScanning(true)}><Icon name="qr" size={54} stroke={2} /><span>Scan QR code</span></button>
            )}
            <div className="divider">or type the 8-digit code</div>
            <form className="stack" onSubmit={(e) => { e.preventDefault(); void validate({ shortCode: code }); }}>
              <Field label="8-digit indent code" hint="Use the code on the approved indent, not a mobile verification code."><input className="big-input code-input" inputMode="numeric" autoComplete="off" placeholder="1234 5678" maxLength={9} value={code} onChange={(e) => setCode(e.target.value.replace(/[^\d ]/g, ''))} /></Field>
              <button className="btn block huge" disabled={busy || code.replace(/\D/g, '').length !== 8}>{busy ? 'Checking…' : 'Check code'}</button>
            </form>
            <ErrorBox error={error} />
          </div>
          <ErrorBox error={claims.error} />
          {claims.items.length ? (
            <>
              <div className="section-head">
                <h2>Finish an earlier fuel entry</h2>
                <span className="muted">{claims.items.length} not saved</span>
              </div>
              <p className="muted">“Start filling” was already tapped for these indents. When filling is finished, open the entry and save the actual litres and amount. If no fuel was given, you can say so there.</p>
              <ul className="tickets">
                {claims.items.map((c) => (
                  <Ticket
                    key={c.indentId}
                    t={{ fuelType: c.fuelType, plate: c.vehicleRegistration, driver: c.personName || undefined, limit: c.limit, status: 'CLAIMED', statusLabel: 'Details not saved', extra: <span className="ticket-extra">Indent {c.number} · Started {clockTime(c.claimedAt)} by {c.claimedBy}{c.stale ? ' · Not saved for over 45 minutes' : ''}</span> }}
                    actions={<button className="btn primary" disabled={busy} onClick={() => void run(async () => setStep({ s: 'supply', view: await api.get<AttendantView>(`/api/redeem/${c.indentId}`) }))}>Finish this entry</button>}
                  />
                ))}
              </ul>
              {claims.hasNextPage ? <button className="btn" disabled={claims.isFetchingNextPage} onClick={() => void claims.fetchNextPage()}>Show older entries</button> : null}
            </>
          ) : null}
        </>
      ) : null}

      {step.s === 'check' ? (
        <>
          <PumpCheck v={step.view} onStart={() => { setError(null); setConfirmClaim(true); }} onBack={reset} />
          <ConfirmDialog
            open={confirmClaim}
            title="Start filling now?"
            lines={[['Driver', step.view.personName || 'Not specified'], ['Vehicle', step.view.vehicleRegistration ? plateText(step.view.vehicleRegistration) : '—'], ['Fuel', step.view.fuelLabel ?? ''], [step.view.fullTank ? 'Full tank, stop at' : 'Up to', step.view.limitType === 'AMOUNT' ? inr(step.view.maxAmountPaise) : litres(step.view.maxQuantity, step.view.unit)]]}
            confirmLabel="Yes, start filling"
            busy={busy}
            error={error}
            onClose={() => setConfirmClaim(false)}
            onConfirm={() => void run(async () => {
              const k = actionKey(`claim:${step.view.indentId}`);
              const view = await api.post<AttendantView>('/api/redeem/claim', step.token, { idempotencyKey: k.key });
              k.done();
              setConfirmClaim(false);
              setStep({ s: 'supply', view });
            })}
          />
        </>
      ) : null}

      {step.s === 'supply' ? <SupplyForm view={step.view} onDone={(f) => setStep({ s: 'done', fulfilment: f, view: step.view })} onCancelled={reset} /> : null}
      {step.s === 'done' ? <AckStep f={step.fulfilment} view={step.view} onFinish={reset} /> : null}
    </div>
  );
}

const STAMPS: Record<string, string> = {
  TOKEN_ALREADY_USED: 'USED',
  INDENT_EXPIRED: 'EXPIRED',
  INDENT_REVOKED: 'STOPPED',
  INDENT_CANCELLED: 'CANCELLED',
  ALREADY_CLAIMED: 'IN USE',
  WRONG_LOCATION: 'OTHER PUMP',
};

function fuelToneFromLabel(label?: string) {
  const l = (label ?? '').toLowerCase();
  return l.includes('diesel') ? 'diesel' : l.includes('petrol') ? 'petrol' : l.includes('cng') ? 'cng' : 'other';
}

function speak(text: string) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = 'en-IN';
  u.rate = 0.9;
  window.speechSynthesis.speak(u);
}

/** Full-screen answer for the attendant: green "OK, fill fuel" or red "Do not fill". */
function PumpCheck({ v, onStart, onBack }: { v: AttendantView; onStart: () => void; onBack: () => void }) {
  const { me } = useSession();
  const ok = v.valid;
  const plate = v.vehicleRegistration ? plateText(v.vehicleRegistration) : '';
  const stopAt = v.limitType === 'AMOUNT' ? rs(v.maxAmountPaise) : `${Number(v.maxQuantity ?? 0)} ${v.unit}`;
  const spokenLimit = v.limitType === 'AMOUNT' ? `${BigInt(v.maxAmountPaise ?? '0') / 100n} rupees` : `${Number(v.maxQuantity ?? 0)} litres`;
  const sayIt = () => speak(ok
    ? `OK. Fill ${v.fuelLabel ?? 'fuel'}. Vehicle ${plate ? plate.split('').join(' ') : 'not given'}. ${v.fullTank ? 'Fill the tank. ' : ''}Stop at ${spokenLimit}.`
    : `Do not fill. ${v.problem?.message ?? ''}`);
  const stamp = !ok ? STAMPS[v.problem?.code ?? ''] : undefined;
  return (
    <div className={`pump-screen ${ok ? 'ok' : 'no'}`} role="region" aria-label={ok ? 'OK to fill' : 'Do not fill'}>
      <div className="pump-inner">
        <div className="pump-top">
          <div className="where"><small>{v.locationName}</small><strong>{me?.user.name}</strong></div>
          <button type="button" className="round-btn" aria-label="Read aloud" onClick={sayIt}><Icon name="speaker" /></button>
        </div>
        <div className="verdict" role={ok ? 'status' : 'alert'}>
          <div className="circle"><Icon name={ok ? 'check' : 'cross'} size={46} stroke={3} /></div>
          <div>
            <h1>{ok ? 'OK, fill fuel' : 'Do not fill'}</h1>
            <p>{ok ? (plate ? 'Check that the vehicle number and fuel type match.' : 'Check the driver’s name and fuel type before filling.') : v.problem?.message}</p>
          </div>
        </div>
        {!ok ? <div className="pump-reason">Do not give fuel using this code. Ask the pump manager or the driver’s company to check the problem shown above.</div> : null}
        <section className={`pump-ticket ${ok ? '' : 'used'}`} aria-label="Indent">
          <div className={`head ${ok ? `fuel-${fuelToneFromLabel(v.fuelLabel)}` : ''}`}>
            <span className="fuel"><Icon name="fuel" size={24} />{v.fuelLabel ?? v.itemDescription}</span>
            <span className="company">{v.buyerName}</span>
          </div>
          <div className="body">
            {plate ? <NumberPlate reg={v.vehicleRegistration} size="lg" /> : <strong style={{ fontSize: '1.2rem' }}>No vehicle number – ask the driver</strong>}
            {v.fullTank ? <div className="instruction">Fill the tank – stop at {stopAt}</div> : null}
            <div className="figures">
              <div><small>Stop at</small><span className="n">{stopAt}</span></div>
              {v.approxQuantity ? <div><small>About this many litres</small><span className="n">{Number(v.approxQuantity).toFixed(1)} L</span></div> : v.unitPricePaise ? <div><small>Price per {v.unit === 'L' ? 'litre' : v.unit}</small><span className="n">{rs(v.unitPricePaise)}</span></div> : null}
            </div>
            <div className="foot">
              <span>Driver: {v.personName || 'not given'}</span>
              {v.expiresAt ? <span>Use before {clockTime(v.expiresAt)}</span> : null}
              {v.requireReceiptPhoto ? <span>Receipt photo needed</span> : null}
              {v.requireOdometer ? <span>Vehicle km reading needed</span> : null}
            </div>
          </div>
          {stamp ? <div className="stamp" aria-hidden="true">{stamp}</div> : null}
        </section>
        <div className="pump-actions">
          {ok ? (
            <>
              <button type="button" className="pump-main" onClick={onStart}><Icon name="fuel" size={28} stroke={2.2} />Start filling</button>
              <button type="button" className="pump-alt" onClick={onBack}>Details do not match – go back</button>
            </>
          ) : (
            <button type="button" className="pump-main" onClick={onBack}><Icon name="qr" size={26} stroke={2.2} />Check another indent</button>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReceiptScan {
  id: string;
  attachmentId: string;
  suggestions: { quantity?: string; amountPaise?: string; unitPricePaise?: string; vehicleRegistration?: string; fuelType?: string; receiptNumber?: string; receiptDate?: string; warnings: string[] };
  lines: { text: string; confidence: number }[];
}

function paiseInput(value: string) {
  const amount = BigInt(value);
  return `${amount / 100n}.${(amount % 100n).toString().padStart(2, '0')}`;
}

function SupplyForm({ view, onDone, onCancelled }: { view: AttendantView; onDone: (f: FulfilmentView) => void; onCancelled: () => void }) {
  const { can } = useSession();
  const [qty, setQty] = useState('');
  const [amount, setAmount] = useState('');
  const [odo, setOdo] = useState('');
  const [odoReason, setOdoReason] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState('');
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [scan, setScan] = useState<ReceiptScan | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [driverName, setDriverName] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const pendingBody = useRef<Record<string, unknown> | null>(null);
  const [locked, setLocked] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [release, setRelease] = useState(false);
  const [note, setNote] = useState('');
  const { busy, error, run, setError } = useBusy();
  useEffect(() => {
    if (!photo) { setPhotoUrl(''); return; }
    const url = URL.createObjectURL(photo);
    setPhotoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);
  const uploadPhoto = async () => {
    if (attachmentId) return attachmentId;
    if (!photo) return null;
    const fd = new FormData();
    fd.append('purpose', 'SUPPLY_EVIDENCE');
    fd.append('file', photo);
    const id = (await api.post<{ id: string }>('/api/attachments', fd)).id;
    setAttachmentId(id);
    return id;
  };
  let qMilli: bigint | null = null;
  try {
    qMilli = qty ? parseQuantity(qty) : null;
  } catch {
    qMilli = null;
  }
  const computed = qMilli && view.unitPricePaise ? amountForQuantity(qMilli, BigInt(view.unitPricePaise)) : null;
  const amountPaise = rupeesToPaise(amount);
  const charged = amountPaise ? BigInt(amountPaise) : computed;
  const over = view.limitType === 'AMOUNT' && view.maxAmountPaise && charged !== null && charged > BigInt(view.maxAmountPaise);
  // Dispenser and calculated amounts can differ by rounding; anything over a rupee is worth a second look.
  const mismatch = amountPaise && computed !== null && (BigInt(amountPaise) - computed > 100n || computed - BigInt(amountPaise) > 100n);
  const odoLower = (error as { code?: string } | null)?.code === 'ODOMETER_LOWER';
  const plate = view.vehicleRegistration || vehicleRegistration.trim();
  const stopAt = view.limitType === 'AMOUNT' ? rs(view.maxAmountPaise) : `${Number(view.maxQuantity ?? 0)} ${view.unit}`;
  const disabled = busy || locked;
  const photoMissing = view.requireReceiptPhoto && !photo && !attachmentId;
  const blockers = saveBlockers({ litresTyped: qty, litresValid: Boolean(qMilli), photoMissing, scanNotReviewed: Boolean(scan && !reviewed), confirmed, over: Boolean(over) });
  const canSave = !busy && blockers.length === 0;
  // Save stays tappable: with something missing it shows what and jumps to the photo step if that is it.
  const photoStep = useRef<HTMLElement>(null);
  const [tried, setTried] = useState(false);
  const trySave = () => {
    if (canSave) { void save(); return; }
    setTried(true);
    if (photoMissing) photoStep.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const save = () => run(async () => {
    const id = await uploadPhoto();
    // Preserve the exact body after an uncertain response, including the uploaded photo.
    const k = actionKey(`supply:${view.indentId}`);
    const body = pendingBody.current ?? {
      quantity: quantityToWire(qMilli!),
      amountPaise: amountPaise ?? undefined,
      odometer: odo ? Number(odo) : undefined,
      odometerExceptionReason: odoReason || undefined,
      evidenceIds: id ? [id] : [],
      receiptScanId: scan?.id,
      receiptReviewed: scan ? reviewed : undefined,
      driverName: !view.personName ? driverName.trim() || undefined : undefined,
      vehicleRegistration: !view.vehicleRegistration ? vehicleRegistration.trim() || undefined : undefined,
      confirmSupplied: true,
    };
    pendingBody.current = body;
    setLocked(true);
    let f: FulfilmentView;
    try { f = await api.post<FulfilmentView>(`/api/redeem/${view.indentId}/supply`, body, { idempotencyKey: k.key }); }
    catch (err) {
      const failure = err as { status?: number; code?: string };
      if (failure.status && failure.status < 500 && failure.code !== 'REQUEST_IN_PROGRESS') { pendingBody.current = null; setLocked(false); k.done(); }
      throw err;
    }
    k.done();
    pendingBody.current = null;
    setLocked(false);
    onDone(f);
  });

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Save the fuel given</h1>
          <p className="muted" style={{ margin: 0 }}>After filling, copy the actual litres and amount from the pump display or receipt.</p>
        </div>
      </div>
      <Ticket
        as="div"
        t={{ fuelType: fuelTypeFromLabel(view.fuelLabel), plate: view.vehicleRegistration, driver: view.personName || undefined, limit: <><b>stop at {stopAt}</b>{view.unitPricePaise ? ` · ${rs(view.unitPricePaise)} per litre` : ''}</> }}
      />
      {photoMissing ? <div className="alert warn" role="status"><strong>Photo needed before saving.</strong> This customer requires a photo of the receipt or pump display. Take it in step 1 below.</div> : null}
      {view.fullTank ? <p className="alert warn">Fill the tank – stop at {stopAt}. Enter the litres actually filled.</p> : null}
      {!view.personName ? <Field label="Driver name at pump (optional)"><input value={driverName} disabled={disabled} onChange={(e) => setDriverName(e.target.value)} maxLength={120} /></Field> : null}
      {!view.vehicleRegistration ? <Field label="Vehicle number at pump (optional)"><input value={vehicleRegistration} disabled={disabled} onChange={(e) => setVehicleRegistration(e.target.value.toUpperCase())} maxLength={24} /></Field> : null}

      <section className="step" ref={photoStep}>
        <div className="step-head"><span className="step-no">1</span><span>Photo of receipt or pump display</span><span className="muted">{view.requireReceiptPhoto ? '(required)' : '(optional)'}</span></div>
        <p className="muted">{view.receiptScanningEnabled ? 'Take a clear photo of the printed receipt to read the litres and amount automatically. Check the numbers before saving.' : 'Keep a photo with this fuel entry. Enter the litres and amount below.'}</p>
        <div className="row" style={{ alignItems: 'stretch', flexWrap: 'nowrap' }}>
          <label className={`photo-tile${photoMissing ? ' needed' : ''}`}>
            <Icon name="camera" size={28} />
            {photo ? 'Take another photo' : photoMissing ? 'Take photo (required)' : 'Take photo'}
            <input className="sr-only" type="file" accept="image/jpeg,image/png,image/webp" capture="environment" disabled={disabled} onChange={(e) => { setPhoto(e.target.files?.[0] ?? null); setAttachmentId(null); setScan(null); setReviewed(false); setError(null); }} />
          </label>
          {view.receiptScanningEnabled ? <button className="btn read-btn" disabled={!photo || disabled || Boolean(scan)} onClick={() => void run(async () => {
            const id = await uploadPhoto();
            const result = await api.post<ReceiptScan>(`/api/redeem/${view.indentId}/receipt-scan`, { attachmentId: id });
            setAttachmentId(result.attachmentId);
            setScan(result);
            setReviewed(false);
          })}>{busy && photo && !scan ? 'Reading…' : 'Read numbers from photo'}</button> : null}
        </div>
        {photoUrl ? <img src={photoUrl} alt="Photo to check" className="photo-preview" /> : null}
        {scan ? (
          <div className="card stack">
            <h3>Numbers read from the photo – check them</h3>
            <p>Litres: {scan.suggestions.quantity ?? 'not found'} · Amount: {scan.suggestions.amountPaise ? inr(scan.suggestions.amountPaise) : 'not found'} · Rate: {scan.suggestions.unitPricePaise ? inr(scan.suggestions.unitPricePaise) : 'not found'}</p>
            <p>{[scan.suggestions.fuelType, scan.suggestions.receiptNumber, scan.suggestions.receiptDate, scan.suggestions.vehicleRegistration].filter(Boolean).join(' · ')}</p>
            {scan.suggestions.warnings.map((warning) => <div key={warning} className="alert warn">{warning}</div>)}
            <button className="btn" disabled={disabled} onClick={() => {
              if (scan.suggestions.quantity) setQty(scan.suggestions.quantity);
              if (scan.suggestions.amountPaise) setAmount(paiseInput(scan.suggestions.amountPaise));
              if (!view.vehicleRegistration && scan.suggestions.vehicleRegistration) setVehicleRegistration(scan.suggestions.vehicleRegistration);
              setReviewed(false);
            }}>Use these numbers</button>
            <details><summary>Text read from the photo</summary><pre style={{ whiteSpace: 'pre-wrap' }}>{scan.lines.map((line) => line.text).join('\n')}</pre></details>
          </div>
        ) : null}
      </section>

      <section className="step">
        <label className="step-head" htmlFor="litres"><span className="step-no">2</span><span>Litres on the pump</span></label>
        <div className="unit-input big">
          <input id="litres" inputMode="decimal" value={qty} disabled={disabled} onChange={(e) => setQty(cleanLitresInput(e.target.value))} placeholder="Enter litres" autoFocus />
          <span>L</span>
        </div>
      </section>

      <div>
        <label className="form-label" htmlFor="amount">Amount on the pump (₹)</label>
        <div className="unit-input">
          <span>₹</span>
          <input id="amount" inputMode="decimal" value={amount} disabled={disabled} onChange={(e) => setAmount(e.target.value)} placeholder={computed !== null ? paiseInput(computed.toString()) : '0.00'} />
        </div>
        {computed !== null && qMilli ? (
          mismatch
            ? <p className="hint-line warn">Does not match {quantityToWire(qMilli)} L × {rs(view.unitPricePaise)} = {formatINR(computed)}. Check the pump display.</p>
            : amountPaise
              ? <p className="hint-line ok"><Icon name="check" size={18} stroke={2.6} />Matches {quantityToWire(qMilli)} L × {rs(view.unitPricePaise)}{view.limitType === 'AMOUNT' && !over ? ` · under ${stopAt}` : ''}</p>
              : <p className="hint-line">Leave empty to use {quantityToWire(qMilli)} L × {rs(view.unitPricePaise)} = {formatINR(computed)}</p>
        ) : null}
      </div>
      {over ? <div className="alert bad">More than approved. Do not fill more. The company must approve a new amount first.</div> : null}

      <Field label={`Vehicle km reading${view.requireOdometer ? ' (required)' : ' (optional)'}`} hint="Copy the total kilometres shown on the vehicle’s dashboard (odometer)."><input inputMode="numeric" value={odo} disabled={disabled} onChange={(e) => setOdo(e.target.value.replace(/\D/g, ''))} /></Field>
      {odoLower && can('supplier.claims.resolve') ? <Field label="Why is the km reading lower than last time?" hint="The pump manager must explain this before saving."><input value={odoReason} disabled={disabled} onChange={(e) => setOdoReason(e.target.value)} /></Field> : null}
      {scan ? <label className="check check-card"><input type="checkbox" checked={reviewed} disabled={disabled} onChange={(e) => setReviewed(e.target.checked)} /> I checked the photo and corrected the litres and amount.</label> : null}
      <label className="check check-card"><input type="checkbox" checked={confirmed} disabled={disabled} onChange={(e) => setConfirmed(e.target.checked)} /> {plate ? `I gave this fuel to ${plateText(plate)}` : 'I gave this fuel to this vehicle'}</label>
      {locked && !busy ? <div className="alert warn">The last save may have gone through. Press Save again to check – nothing will be saved twice.</div> : null}
      <ErrorBox error={error} />
      <div className="stack" style={{ marginTop: '.5rem' }}>
        <button className={`btn primary block huge${canSave ? '' : ' looks-disabled'}`} disabled={busy} aria-disabled={!canSave} onClick={trySave}>{busy ? 'Saving…' : charged !== null ? `Save ${rs(charged.toString())}` : 'Save'}</button>
        {!busy && blockers.length ? <div className={`alert ${tried ? 'bad' : 'warn'}`} role="status"><strong>To save:</strong><ul style={{ margin: '.25rem 0 0', paddingLeft: '1.2rem' }}>{blockers.map((b) => <li key={b}>{b}</li>)}</ul></div> : null}
        <p className="muted" style={{ textAlign: 'center', margin: 0 }}>Saving adds this amount to the customer’s account. Check the details first. Ask your manager if a saved entry needs correcting.</p>
        <button className="btn ghost block danger-text" disabled={disabled} onClick={() => setRelease(true)}>No fuel was given</button>
      </div>
      <ConfirmDialog open={release} title="No fuel was given?" lines={[['Vehicle', view.vehicleRegistration ? plateText(view.vehicleRegistration) : '—']]} confirmLabel="Confirm no fuel given" danger busy={busy} error={error} onClose={() => setRelease(false)} onConfirm={() => void run(async () => {
        const k = actionKey(`release:${view.indentId}`);
        await api.post(`/api/redeem/${view.indentId}/release`, { note }, { idempotencyKey: k.key });
        k.done();
        setRelease(false);
        onCancelled();
      })}>
        <p className="muted">Use this only if no fuel was given. The customer will not be charged. This indent is cancelled and its code can no longer be used.</p>
        <Field label="Why was no fuel given?" hint="For example: the driver left before filling."><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </ConfirmDialog>
    </div>
  );
}

function fuelTypeFromLabel(label?: string) {
  const tone = fuelToneFromLabel(label);
  return tone === 'petrol' ? 'PETROL' : tone === 'diesel' ? 'DIESEL' : tone === 'cng' ? 'CNG' : undefined;
}

function AckStep({ f, view, onFinish }: { f: FulfilmentView; view: AttendantView; onFinish: () => void }) {
  const channels = useQuery({ queryKey: ['code-channels'], queryFn: () => api.get<{ whatsappAck: boolean }>('/api/auth/config', { orgId: null }) });
  const whatsapp = channels.data?.whatsappAck === true;
  const [consent, setConsent] = useState(false);
  const [wait, setWait] = useState(0);
  const [challenge, setChallenge] = useState<{ challengeId: string; deliveryStatus: string; phoneMasked: string; withDetails?: boolean; deliveryChannel?: 'SMS' | 'WHATSAPP'; fallback?: boolean } | null>(null);
  const [code, setCode] = useState('');
  const [done, setDone] = useState(f.ack.status !== 'PENDING');
  const { busy, error, run } = useBusy();
  const sent = useRef(false);
  const send = (channel: 'SMS' | 'WHATSAPP') => run(async () => {
    setChallenge(await api.post(`/api/fulfilments/${f.id}/ack/send`, { channel, whatsappConsent: channel === 'WHATSAPP' && consent }));
    setCode(''); setWait(30);
  });
  useEffect(() => {
    if (!wait) return;
    const timer = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);
  useEffect(() => {
    // Preserve SMS for older installations. WhatsApp always requires the driver's agreement.
    if (channels.isPending || whatsapp || sent.current || f.ack.status !== 'PENDING' || !f.person?.personId) return;
    sent.current = true;
    void run(async () => { setChallenge(await api.post(`/api/fulfilments/${f.id}/ack/send`)); setWait(30); });
  }, [channels.isPending, whatsapp, f.id, f.ack.status, f.person?.personId, run]);
  const line = f.lines[0];
  const driver = f.person?.name?.split(' (')[0] || 'the driver';
  return (
    <div className="stack">
      <section className="saved-hero">
        <div className="row" style={{ flexWrap: 'nowrap', gap: '.85rem' }}>
          <div className="saved-tick"><Icon name="check" size={34} stroke={3} /></div>
          <div>
            <h1>Fuel entry saved</h1>
            <p>Added to {view.buyerName || 'the company'}’s account · {clockTime(f.occurredAt)}</p>
          </div>
        </div>
        <div className="saved-figures">
          <div>
            <div className="hero-amount">{inr(f.totalPaise)}</div>
            <div>{litres(line.quantity, line.unit)} {line.description}</div>
          </div>
          {f.vehicle?.registrationNumber ? <NumberPlate reg={f.vehicle.registrationNumber} size="sm" /> : null}
        </div>
      </section>

      {f.ack.status === 'NOT_REQUIRED' ? null : done ? <div className="alert ok"><strong>Driver confirmed.</strong></div> : !f.person?.personId ? (
        <div className="alert">No driver phone for an SMS code. The company can confirm this fill later.</div>
      ) : (
        <section className="card stack">
          <div className="row" style={{ flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <span className="menu-icon info"><Icon name="phone" /></span>
            <div>
              <h2 style={{ margin: 0 }}>Ask {driver} to confirm the fuel received</h2>
              <p className="muted">Show the driver the litres and amount above. If correct, ask for their 6-digit confirmation code.</p>
              {challenge ? (
                challenge.deliveryChannel === 'WHATSAPP' && ['QUEUED', 'SENT'].includes(challenge.deliveryStatus)
                  ? <div className="alert info">Ask the driver to check WhatsApp on {challenge.phoneMasked}. Delivery may take a moment. The message contains only the code.</div>
                  : challenge.deliveryStatus === 'SENT'
                  ? challenge.withDetails === false
                    ? <div className="alert info">Code sent to {challenge.phoneMasked}. The SMS has only the code: show the driver the litres and amount above before they read it out.</div>
                    : <p className="muted" style={{ margin: 0 }}>Code sent to {challenge.phoneMasked}, with the litres and amount.</p>
                  : <div className="alert warn">SMS could not be sent ({challenge.deliveryStatus.toLowerCase().replace('_', ' ')}). The company can confirm this fill later.</div>
              ) : <p className="muted" style={{ margin: 0 }}>{whatsapp ? 'Ask the driver where they want to receive the code.' : 'Sending the driver a 6-digit code by SMS…'}</p>}
              {challenge?.fallback ? <p className="muted">WhatsApp was unavailable. Check SMS instead.</p> : null}
            </div>
          </div>
          {whatsapp ? <label className="check"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} /> Driver agrees to receive this confirmation code from SuperIndent on WhatsApp, with SMS as a backup.</label> : null}
          {whatsapp && challenge?.deliveryChannel !== 'SMS' ? <button className="btn block" disabled={busy || wait > 0 || !consent} onClick={() => void send('WHATSAPP')}>{wait > 0 ? `Send again in ${wait}s` : 'Send code on WhatsApp'}</button> : null}
          <Field label="Code from driver">
            <input className="big-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
          </Field>
          <ErrorBox error={error} />
          <button className="btn primary block huge" disabled={busy || code.length !== 6 || !challenge} onClick={() => void run(async () => { await api.post(`/api/fulfilments/${f.id}/ack/confirm`, { challengeId: challenge!.challengeId, code }); setDone(true); })}>Confirm</button>
          <button className="btn ghost block" disabled={busy || wait > 0} onClick={() => void send('SMS')}>{wait > 0 ? `SMS available in ${wait}s` : challenge?.deliveryChannel === 'WHATSAPP' ? 'Use SMS instead' : challenge ? 'Send the code again' : 'Send code by SMS'}</button>
          <p className="muted" style={{ textAlign: 'center', margin: 0 }}>Driver can’t give the code? The fill is still saved. The company can confirm it later.</p>
        </section>
      )}
      <button className="btn block huge outline-strong" onClick={onFinish}>Next customer<Icon name="next" size={22} stroke={2.4} /></button>
    </div>
  );
}
